import React, { useState } from 'react';
import { Appointment } from '../types';

interface MeetingRatingModalProps {
    appointment: Appointment;
    onSubmit: (appointmentId: number, score: number, comment?: string) => void;
    onClose: () => void;
    onSkip: () => void;
    onReportNoShow?: (appointmentId: number, reason?: string) => void;
}

const MeetingRatingModal: React.FC<MeetingRatingModalProps> = ({
    appointment,
    onSubmit,
    onClose,
    onSkip,
    onReportNoShow
}) => {
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showNoShowForm, setShowNoShowForm] = useState(false);
    const [noShowReason, setNoShowReason] = useState('');

    const handleSubmit = async () => {
        if (rating === 0) {
            return;
        }
        setIsSubmitting(true);
        try {
            await onSubmit(appointment.id, rating);
            onClose();
        } catch (error) {
            console.error('Rating submit error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNoShow = async () => {
        setIsSubmitting(true);
        try {
            if (onReportNoShow) {
                await onReportNoShow(appointment.id, noShowReason || undefined);
            }
            onClose();
        } catch (error) {
            console.error('No-show report error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStarColor = (starIndex: number) => {
        const activeRating = hoveredRating || rating;
        if (starIndex <= activeRating) {
            if (activeRating <= 2) return 'text-red-400';
            if (activeRating <= 3) return 'text-yellow-400';
            return 'text-green-400';
        }
        return 'text-gray-300 dark:text-gray-600';
    };

    const getRatingText = () => {
        const activeRating = hoveredRating || rating;
        switch (activeRating) {
            case 1: return 'Çok Kötü 😞';
            case 2: return 'Kötü 😕';
            case 3: return 'Orta 😐';
            case 4: return 'İyi 🙂';
            case 5: return 'Mükemmel 🤩';
            default: return 'Yıldız seçin';
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-scaleIn">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-6 text-white relative overflow-hidden">
                    <button
                        onClick={onSkip}
                        className="absolute top-4 right-4 text-white/50 hover:text-white transition-all transform hover:scale-110 z-20 p-2"
                        title="Kapat"
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <span className="text-2xl">⭐</span>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">Görüşmeyi Değerlendir</h2>
                                <p className="text-white/80 text-sm">Geri bildiriminiz çok değerli!</p>
                            </div>
                        </div>
                        <div className="mt-4 p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                            <p className="text-sm font-medium text-white/90">
                                {appointment.type === 'consultant' ? '👨‍💼 Danışman Görüşmesi' : `👨‍🏫 ${appointment.subject || 'Ders'} Görüşmesi`}
                            </p>
                            <p className="text-xs text-white/70 mt-1">
                                📅 {formatDate(appointment.date)} • ⏰ {appointment.time}
                            </p>
                            {appointment.teacherName && (
                                <p className="text-xs text-white/70 mt-1">
                                    👤 {appointment.teacherName}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {showNoShowForm ? (
                        /* No-Show Feedback Form */
                        <div className="space-y-4">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <span className="text-3xl">❌</span>
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 dark:text-white">Görüşme Gerçekleşmedi</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Lütfen sebebini belirtin (isteğe bağlı)</p>
                            </div>

                            <div className="space-y-2">
                                {[
                                    'Danışman/Öğretmen katılmadı',
                                    'Teknik sorunlar yaşandı',
                                    'Zaman uyuşmazlığı',
                                    'Diğer'
                                ].map(reason => (
                                    <button
                                        key={reason}
                                        onClick={() => setNoShowReason(reason)}
                                        className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all text-sm font-medium ${noShowReason === reason
                                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                                            : 'border-gray-100 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-slate-500'
                                            }`}
                                    >
                                        {reason}
                                    </button>
                                ))}
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => { setShowNoShowForm(false); setNoShowReason(''); }}
                                    className="flex-1 px-4 py-3 rounded-2xl border-2 border-gray-100 dark:border-slate-700 text-gray-500 dark:text-gray-400 font-bold hover:bg-gray-50 dark:hover:bg-slate-700 transition-all active:scale-95"
                                >
                                    ← Geri
                                </button>
                                <button
                                    onClick={handleNoShow}
                                    disabled={isSubmitting}
                                    className="flex-1 px-4 py-3 rounded-2xl font-bold text-white bg-gradient-to-r from-red-500 to-rose-600 shadow-lg hover:shadow-red-300/50 transition-all active:scale-95 disabled:opacity-50"
                                >
                                    {isSubmitting ? 'İşleniyor...' : 'Bildir'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Rating Form */
                        <>
                            <div className="text-center mb-8">
                                <p className="text-gray-600 dark:text-gray-300 mb-6 font-medium text-lg">
                                    Bu görüşmeyi nasıl değerlendirirsiniz?
                                </p>

                                {/* Stars */}
                                <div className="flex justify-center gap-3 mb-4">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            title={`${star} yıldız`}
                                            onClick={() => setRating(star)}
                                            onMouseEnter={() => setHoveredRating(star)}
                                            onMouseLeave={() => setHoveredRating(0)}
                                            className={`transition-all duration-200 transform hover:scale-125 ${getStarColor(star)}`}
                                        >
                                            <svg
                                                className="w-12 h-12 drop-shadow-lg"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                            </svg>
                                        </button>
                                    ))}
                                </div>

                                {/* Rating Text */}
                                <p className={`text-xl font-bold transition-all ${rating === 0 ? 'text-gray-400' :
                                    rating <= 2 ? 'text-red-500' :
                                        rating <= 3 ? 'text-yellow-500' :
                                            'text-green-500'
                                    }`}>
                                    {getRatingText()}
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="space-y-3">
                                <div className="flex gap-4">
                                    <button
                                        onClick={onSkip}
                                        className="flex-1 px-4 py-4 rounded-2xl border-2 border-gray-100 dark:border-slate-700 text-gray-500 dark:text-gray-400 font-bold hover:bg-gray-50 dark:hover:bg-slate-700 transition-all active:scale-95"
                                    >
                                        Şimdi Değil
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={rating === 0 || isSubmitting}
                                        className={`flex-1 px-4 py-4 rounded-2xl font-bold text-white shadow-lg transition-all ${rating === 0
                                            ? 'bg-gray-200 dark:bg-slate-700 cursor-not-allowed text-gray-400 shadow-none'
                                            : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-indigo-300/50 active:scale-95'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                İşleniyor
                                            </span>
                                        ) : (
                                            'Değerlendir ✨'
                                        )}
                                    </button>
                                </div>

                                {/* No-Show Button */}
                                {onReportNoShow && (
                                    <button
                                        onClick={() => setShowNoShowForm(true)}
                                        className="w-full px-4 py-3 rounded-2xl border-2 border-dashed border-red-200 dark:border-red-800/50 text-red-500 dark:text-red-400 font-bold text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-all active:scale-95"
                                    >
                                        ❌ Görüşme Gerçekleşmedi
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MeetingRatingModal;
